import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop, Smartphone } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { ConfirmDialog } from '../../../components/ui/Dialog';
import { applyServerErrors, TextField } from '../../../components/ui/Field';
import { ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import { TotpCard } from '../../platform/components/OpsAndSecurity';
import { useAuth } from '../AuthProvider';
import { authApi } from '../api';
import { newPassword } from './PasswordPages';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword,
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });

export function AccountPage() {
  const { me, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['auth', 'sessions'], queryFn: authApi.sessions });
  const signInPath = me?.user.type === 'platform' ? '/platform/login' : '/login';

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });
  const change = useMutation({
    mutationFn: (v: z.infer<typeof schema>) =>
      authApi.changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword }),
    onSuccess: async () => {
      toast.success('Password changed. Please sign in again.');
      await logout();
      navigate(signInPath);
    },
    onError: (err) =>
      err instanceof ApiError ? applyServerErrors(err.fieldErrors, form.setError as never) : undefined,
  });

  return (
    <PageContainer width="md">
      <PageHeader title="My account" description={`${me?.user.fullName} · ${me?.user.email}`} />
      <div className="space-y-6">
        <Card>
          <CardHeader
            title="Signed-in devices"
            description="Sign out anything you don't recognise."
            actions={
              <ConfirmDialog
                title="Sign out everywhere?"
                description="Every device, including this one, will need to sign in again."
                confirmLabel="Sign out everywhere"
                onConfirm={async () => {
                  await authApi.logoutAll();
                  await logout();
                  navigate(signInPath);
                }}
                trigger={(open) => (
                  <Button size="sm" variant="secondary" onClick={open}>
                    Sign out everywhere
                  </Button>
                )}
              />
            }
          />
          {sessions.isPending ? (
            <TableSkeleton rows={2} />
          ) : sessions.isError ? (
            <CardBody>
              <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
            </CardBody>
          ) : (
            <ul className="divide-y divide-line">
              {sessions.data.map((s) => (
                <li key={s.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                  {s.client === 'mobile' ? (
                    <Smartphone aria-hidden className="size-4 text-fg-muted" />
                  ) : (
                    <Laptop aria-hidden className="size-4 text-fg-muted" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-fg">
                      {s.userAgent ?? (s.client === 'mobile' ? 'Mobile app' : 'Browser')}
                    </p>
                    <p className="text-xs text-fg-subtle">
                      Last active {new Date(s.lastUsedAt).toLocaleString()}
                    </p>
                  </div>
                  {s.current ? (
                    <Badge tone="success">This device</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        await authApi.revokeSession(s.id);
                        await queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
                        toast.success('Device signed out');
                      }}
                    >
                      Sign out
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Change password" description="You will be signed out of all devices." />
          <CardBody>
            <form noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => change.mutate(v))}>
              {change.error &&
              !(change.error instanceof ApiError && change.error.code === 'VALIDATION_FAILED') ? (
                <ErrorState error={change.error} />
              ) : null}
              <TextField
                label="Current password"
                type="password"
                autoComplete="current-password"
                error={form.formState.errors.currentPassword?.message}
                {...form.register('currentPassword')}
              />
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                hint="At least 10 characters"
                error={form.formState.errors.newPassword?.message}
                {...form.register('newPassword')}
              />
              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                error={form.formState.errors.confirm?.message}
                {...form.register('confirm')}
              />
              <div>
                <Button type="submit" loading={change.isPending}>
                  Change password
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
        {me?.user.type === 'platform' ? <TotpCard /> : null}
      </div>
    </PageContainer>
  );
}
